import { useCallback, useState } from "react";

/**
 * «حسب المحطة / حسب الطلب» — WHICH VIEW THIS SCREEN OPENS ON (T5, T13; D170).
 *
 * The default is decided by PERMISSION, never by a role's name: a session
 * that may serve (`ITEM_SERVE`) but not cook (`ITEM_PREPARE`) is an
 * expediter, who reads ORDERS — it opens on the order view. Everyone else
 * opens on the station lanes. A choice made on the device (`triotyKitchenView`)
 * overrides the default, the same shape and storage as the remembered station
 * (`stationPreference.ts`); unreadable storage falls back to the default.
 */

export type KitchenView = "station" | "order";

export const KITCHEN_VIEW_STORAGE_KEY = "triotyKitchenView";

export function defaultKitchenView(grants: { canServe: boolean; canPrepare: boolean }): KitchenView {
  return grants.canServe && !grants.canPrepare ? "order" : "station";
}

export function readKitchenView(): KitchenView | null {
  try {
    const value = localStorage.getItem(KITCHEN_VIEW_STORAGE_KEY);
    return value === "station" || value === "order" ? value : null;
  } catch {
    return null;
  }
}

export function useKitchenViewPreference(grants: { canServe: boolean; canPrepare: boolean }): {
  view: KitchenView;
  setView: (next: KitchenView) => void;
} {
  const [stored, setStored] = useState<KitchenView | null>(readKitchenView);
  const setView = useCallback((next: KitchenView) => {
    setStored(next);
    try {
      localStorage.setItem(KITCHEN_VIEW_STORAGE_KEY, next);
    } catch {
      // Unwritable storage still honours the choice for this page's life.
    }
  }, []);
  return { view: stored ?? defaultKitchenView(grants), setView };
}
